import type { ClientGroup } from '@DigitaleDoerfer/digitale-doerfer-api';
import {
  ClientGroupAccessibilityEnum,
  ClientGroupMembershipStatusEnum
} from '@DigitaleDoerfer/digitale-doerfer-api';
import { useCallback, useMemo, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton } from 'react-native-paper';

import { LockClockIcon, LockIcon, LockOpenIcon, LockResetIcon } from '@/assets';
import { GroupsRoute } from '@/features/groups/navigation/groupsRoute';
import type { ListItemProps } from '@/features/list/components/ListItem';
import { ListItem } from '@/features/list/components/ListItem';
import { ProfileAvatar } from '@/features/media/components/avatar/ProfileAvatar';
import { MediaItemSize } from '@/features/media/types/mediaItem';
import { usePopup } from '@/features/popup/provider/usePopup';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { NavigationListItem } from '@/navigation/components/NavigationListItem';
import { useAppNavigation } from '@/navigation/hooks/useAppNavigation';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

type Props = {
  group: ClientGroup;
  isMyGroup?: boolean;
};

export const GroupListItem: FC<Props> = ({ group, isMyGroup }) => {
  const {
    id,
    name,
    logo,
    shortName,
    memberCount,
    membershipStatus,
    accessibility
  } = group;

  const theme = useAppTheme();
  const { t } = useTranslation(LocalizationNameSpace.GROUPS, {
    keyPrefix: 'components.groupListItem'
  });
  const { showAlert } = usePopup();

  const requiresApproval =
    accessibility === ClientGroupAccessibilityEnum.ApprovalRequired;
  const membershipPending =
    membershipStatus === ClientGroupMembershipStatusEnum.Pending;
  const membershipRejected =
    membershipStatus === ClientGroupMembershipStatusEnum.Rejected;
  const membershipNotMember =
    membershipStatus === ClientGroupMembershipStatusEnum.NotMember;

  const navigationParams = useMemo(() => {
    if (isMyGroup || !requiresApproval) {
      return {
        screen: GroupsRoute.GroupDetail,
        params: { groupId: id, name, shortName, memberCount }
      };
    } else
      return membershipNotMember
        ? {
            screen: GroupsRoute.GroupDescription,
            params: { groupId: id }
          }
        : undefined;
  }, [
    id,
    isMyGroup,
    memberCount,
    membershipNotMember,
    name,
    requiresApproval,
    shortName
  ]);

  const navigation = useAppNavigation();

  const sharedParams: ListItemProps = {
    backgroundColor: theme.colors.surfaceContainerLowest,
    style: { paddingVertical: 2 },
    icon: () => (
      <IconButton
        onPress={
          requiresApproval && !membershipNotMember
            ? undefined
            : (e) => {
                e.preventDefault();
                navigation.navigate(GroupsRoute.GroupDescription, {
                  groupId: id
                });
              }
        }
        icon={() => (
          <ProfileAvatar
            textFallback={{ shortName }}
            mediaItem={logo}
            mediaSize={MediaItemSize.EXTRA_SMALL}
            viewSize={56}
          />
        )}
        size={56}
        // Note: Margin -8 is needed as icon button is adding additional padding to highlight when pressed but here we need the passed size as view size
        style={{ margin: -8 }}
      />
    ),
    title: name,
    description: t('members', { count: memberCount }),
    textOrder: 'column-reverse'
  };

  const handleShowAlert = useCallback(
    (alertType: 'pending' | 'rejected') => {
      showAlert({
        labels: t(`alerts.${alertType}`, {
          returnObjects: true
        }),
        confirmColor: theme.colors.primary
      });
    },
    [showAlert, t, theme.colors.primary]
  );

  return (
    <>
      {navigationParams ? (
        <NavigationListItem
          {...sharedParams}
          {...navigationParams}
          arrowIcon={
            (isMyGroup && !requiresApproval) ||
            !(requiresApproval || membershipPending)
              ? undefined
              : ({ fill }) => {
                  const iconProps = { fill, height: 24, width: 24 };
                  if (isMyGroup) {
                    return <LockOpenIcon {...iconProps} />;
                  }
                  if (requiresApproval) {
                    return <LockIcon {...iconProps} />;
                  }
                }
          }
        />
      ) : (
        <ListItem
          {...sharedParams}
          onPress={() => {
            if (membershipPending) {
              handleShowAlert('pending');
              return;
            }
            if (membershipRejected) {
              handleShowAlert('rejected');
              return;
            }
          }}
          arrowIcon={({ fill }) => {
            const iconProps = { fill, height: 24, width: 24 };
            if (membershipPending) {
              return <LockClockIcon {...iconProps} />;
            }
            if (membershipRejected) {
              return <LockResetIcon {...iconProps} />;
            }
          }}
        />
      )}
    </>
  );
};
